import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import withModal from "../withModal";
import { hideModal } from "../../../actions/actionCreators";
import { requestFlow } from "../../../actions/flowActions";
import { getFlowItemDeployStatuses } from "../../../actions/flowActions";
import { editUrlForFlowItem } from "../../../reducers/flowItems";
import { Link } from "react-router-dom";
import MaterialModal from "../MaterialModal";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import { IAppState } from "../../../types/stores";

const ExpandMore = <i className="material-icons">expand_more</i>;
const ExpandLess = <i className="material-icons">expand_less</i>;

const propTypes = {
    isOpen: PropTypes.bool.isRequired,
    modalProps: PropTypes.shape({
        item: PropTypes.shape({
            DecisionTimestamp: PropTypes.string,
            DeploySetting: PropTypes.number,
            Destination: PropTypes.string,
            FileName: PropTypes.string,
            FlowCompanyId: PropTypes.number,
            FlowDeploymentId: PropTypes.number,
            FlowId: PropTypes.number,
            FlowIsActive: PropTypes.bool,
            FlowItemId: PropTypes.number,
            FlowItemName: PropTypes.string,
            FlowItemQty: PropTypes.string,
            FlowItemType: PropTypes.string,
            FlowName: PropTypes.string,
            HasQATable: PropTypes.bool,
            HasResultTable: PropTypes.bool,
            IsLocked: PropTypes.bool,
            IsLockedPermanently: PropTypes.bool,
            CanShowCount: PropTypes.bool,
            LayoutId: PropTypes.number,
            LayoutName: PropTypes.string,
            RequestTimestamp: PropTypes.string,
            Requestee: PropTypes.string,
            RequesteeCompany: PropTypes.string,
            RequesteeCompanyId: PropTypes.number,
            RequesteeEmail: PropTypes.string,
            RequesteeFirstName: PropTypes.string,
            RequesteeId: PropTypes.number,
            RequesteeLastName: PropTypes.string,
            RequesteeReason: PropTypes.string,
            Requestor: PropTypes.string,
            RequestorCompany: PropTypes.string,
            RequestorCompanyId: PropTypes.number,
            RequestorEmail: PropTypes.string,
            RequestorFirstName: PropTypes.string,
            RequestorLastName: PropTypes.string,
            RequestorReason: PropTypes.string,
            RequestorTimestamp: PropTypes.string,
            WorkflowId: PropTypes.number,
            WorkflowStatusId: PropTypes.number,
            WorkflowStatusName: PropTypes.string,
            WorkflowTypeId: PropTypes.number,
            WorkflowTypeName: PropTypes.string,
        }),
        linkFlow: PropTypes.string,
        linkFlowItem: PropTypes.string,
        listType: PropTypes.string,
        requestDate: PropTypes.string,
        decisionTimestamp: PropTypes.string,
        showLayoutAndQuantity: PropTypes.bool,
    }).isRequired,
};

type DeploymentDetailsModalProps = PropTypes.InferProps<typeof propTypes>;

const DeploymentDetailsModal: React.FC<DeploymentDetailsModalProps> = ({
    isOpen,
    modalProps,
}: DeploymentDetailsModalProps) => {
    const dispatch = useDispatch();

    const [statusOpen, setStatusOpen] = useState(false);

    const loading = useSelector((state: IAppState) => state.flowDeploymentQueue.loading || {});
    const audienceStatuses = useSelector((state: IAppState) => state.flowDeploymentQueue.audienceStatuses || []);
    const flowItems = useSelector((state: IAppState) => state.flowItems.byId);

    useEffect(() => {
        if (
            modalProps.item &&
            modalProps.item.FlowItemId &&
            modalProps.item.FlowCompanyId &&
            modalProps.item.WorkflowId
        ) {
            dispatch(requestFlow(modalProps.item.FlowId));
            dispatch(
                getFlowItemDeployStatuses(
                    modalProps.item.FlowItemId,
                    modalProps.item.FlowCompanyId,
                    modalProps.item.WorkflowId
                )
            );
        }
    }, [dispatch, modalProps]);

    const hide = () => dispatch(hideModal("DEPLOYMENT_DETAILS"));

    const getFlowItemName = (itemId: number) => {
        const flowItem = flowItems[itemId];
        if (flowItem == null || flowItem.FlowItemName.trim() == "") {
            return "Unnamed Flow Item";
        }

        let flowItemName = "Flow Item (" + flowItem.FlowItemType + "): " + flowItem.FlowItemName;

        return flowItemName;
    };

    const linkFlowItem = (flowItemId: number) => {
        const flowItemUrl = editUrlForFlowItem(modalProps.item!.FlowId!, flowItemId);
        const flowItemName = getFlowItemName(flowItemId);
        return (
            <Tooltip title={flowItemName}>
                <Link to={flowItemUrl ? flowItemUrl : ""} onClick={hide}>
                    {flowItemName}
                </Link>
            </Tooltip>
        );
    };

    let title;
    let modalText;
    if (modalProps.item != null) {
        title = modalProps.linkFlow;
        let status =
            loading && loading.tapad ? (
                <ListItem>
                    <ListItemText primary="Status: " secondary={"Loading..."} />
                </ListItem>
            ) : (
                <ListItem>
                    <ListItemText primary="Status: " secondary={modalProps.item.WorkflowStatusName} />
                </ListItem>
            );

        if (audienceStatuses.length > 0) {
            status = (
                <div>
                    <ListItem button key="status-dropdown" onClick={() => setStatusOpen(!statusOpen)}>
                        <ListItemText primary="Status:" secondary={modalProps.item.WorkflowStatusName} />
                        {statusOpen ? ExpandLess : ExpandMore}
                    </ListItem>

                    <div style={{ marginLeft: "25px" }}>
                        <Collapse key="tapad-status-list" component="li" in={statusOpen} timeout="auto" unmountOnExit>
                            <List dense>
                                {audienceStatuses.map((statusItem, i) => (
                                    <>
                                        <ListItemText
                                            key={"status-" + i}
                                            style={{ textTransform: "capitalize" }}
                                            primary={linkFlowItem(statusItem.parent_flow_item_id)}
                                            secondary={
                                                statusItem.status == "IN_PROGRESS"
                                                    ? "In Progress"
                                                    : statusItem.status.toLowerCase()
                                            }
                                        />
                                        {statusItem.audience_name != null && statusItem.audience_name != "" && (
                                            <ListItemText
                                                key={"audience-name-" + i}
                                                style={{ textTransform: "capitalize" }}
                                                secondary={`AUDIENCE_NAME: ${statusItem.audience_name.toLowerCase()}`}
                                            />
                                        )}
                                        {statusItem.tt_audience_id != null && statusItem.tt_audience_id > 0 && (
                                            <ListItemText
                                                key={"tt_audience-id-" + i}
                                                style={{ textTransform: "capitalize" }}
                                                secondary={`TT_AUDIENCE_ID: ${statusItem.tt_audience_id}`}
                                            />
                                        )}
                                        {statusItem.details != null && statusItem.details != "" && (
                                            <ListItemText
                                                key={"details-" + i}
                                                style={{ textTransform: "capitalize" }}
                                                secondary={`DETAILS: ${statusItem.details.toLowerCase()}`}
                                            />
                                        )}
                                        {statusItem.created != null && (
                                            <ListItemText
                                                key={"created-" + i}
                                                style={{ textTransform: "capitalize" }}
                                                secondary={`CREATED: ${statusItem.createdString}`}
                                            />
                                        )}
                                        {statusItem.created != null && (
                                            <ListItemText
                                                key={"updated-" + i}
                                                style={{ textTransform: "capitalize" }}
                                                secondary={`UPDATED: ${statusItem.updatedString}`}
                                            />
                                        )}
                                    </>
                                ))}
                            </List>
                        </Collapse>
                    </div>
                </div>
            );
        }

        modalText = (
            <div className="listForModal">
                <List dense>
                    <ListItem>
                        <ListItemText primary="Flow: " secondary={modalProps.linkFlow} />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Item: " secondary={modalProps.linkFlowItem} />
                    </ListItem>
                    {status}
                    <ListItem>
                        <ListItemText primary="Type: " secondary={modalProps.item.WorkflowTypeName} />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Requestor: " secondary={modalProps.item.Requestor} />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Request Date: " secondary={modalProps.requestDate} />
                    </ListItem>
                    {modalProps.showLayoutAndQuantity && (
                        <ListItem>
                            <ListItemText primary="Layout: " secondary={modalProps.item.LayoutName} />
                        </ListItem>
                    )}
                    <ListItem>
                        <ListItemText primary="Destination: " secondary={modalProps.item.Destination} />
                    </ListItem>
                    {modalProps.showLayoutAndQuantity && (
                        <ListItem>
                            <ListItemText
                                primary="Quantity: "
                                secondary={modalProps.item.CanShowCount ? modalProps.item.FlowItemQty : "-"}
                            />
                        </ListItem>
                    )}
                    {modalProps.listType == "Historical" ? (
                        <>
                            <ListItem>
                                <ListItemText
                                    primary="Released By: "
                                    secondary={
                                        modalProps.item.FlowItemType == "discovery" ||
                                            modalProps.item.FlowItemType == "offload"
                                            ? modalProps.item.Requestor
                                            : modalProps.item.Requestee
                                    }
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Decision Date: " secondary={modalProps.decisionTimestamp} />
                            </ListItem>
                        </>
                    ) : (
                        ""
                    )}
                    {modalProps.item.FlowItemType == "offload" ? (
                        <ListItem>
                            <ListItemText primary="File Name: " secondary={modalProps.item.FileName} />
                        </ListItem>
                    ) : (
                        ""
                    )}
                </List>
            </div>
        );
    }

    const modal = (
        <MaterialModal
            title={<span>{title || ""} Details</span>}
            isOpen={isOpen}
            onClose={hide}
            id="details-for"
            fullWidth
            maxWidth="xs"
            showPrimaryButton={false}
            cancelButtonText="Close"
        >
            {modalText || ""}
        </MaterialModal>
    );

    return modal;
};

export default withModal(DeploymentDetailsModal, "DEPLOYMENT_DETAILS");
